// src/features/fb/cookies/results/CookiesResultsSection.jsx
// Cookies-specific Results Section with Cost Analysis, Image, Table, and Waterfall Chart
// Customized for F&B Cookies commodity

import React from "react";
import { Box, Typography, Paper, Tabs, Tab } from "@mui/material";
import ImageWithRegenerate from "../../../../shared/modules/ImageWithRegenerate";
import PerlCostTable from "../../../../shared/components/PerlCostTable";
import CostWaterfall from "../../../../shared/components/CostWaterfall";
import { theme } from "../../../../design/theme";

const { perlColors, perlTypography } = theme.custom;

/**
 * CookiesResultsSection - Display cost analysis results with image, table, and waterfall chart
 * Customized for F&B Cookies commodity with Form_FB_New.jsx schema structure
 *
 * Risk Data Schema: { summary, matrix, risk_items, assumptions }
 * Substitute Data Schema: { summary, ideas, prioritization, assumptions }
 *
 * @param {Object} props
 * @param {Object} props.costData - Cost analysis result with breakdown
 * @param {Object} props.riskData - Risk analysis result
 * @param {Object} props.substituteData - Substitute recommendations result
 * @param {string} props.productImageUrl - Product image URL
 * @param {boolean} props.generatingImage - Image generation loading state
 * @param {Function} props.onRegenerateImage - Callback for regenerating image
 * @param {string} props.productName - Product name
 * @param {string} props.brandColor - Brand color (default: #ff6034)
 * @param {Object} props.assumptions - Object with subsections: {Formulation: [...], Process: [...], Packaging: [...], OHP: [...]}
 * @param {Object} props.keyInsights - Key insights object with cost_savings and strategic arrays
 * @param {Array} props.costRows - Cost table rows: [{ key, label, value }]
 * @param {number} props.costTotal - Optional cost total override for table percentage calculations
 * @param {Array} props.breakdownRows - Optional row configuration for PerlCostTable
 */

export default function CookiesResultsSection({
  costData,
  costRows = [],
  costTotal = undefined,
  riskData,
  substituteData,
  productImageUrl,
  generatingImage = false,
  onRegenerateImage,
  productName = "Product",
  productData = null,
  brandColor = "#ff6034",
  assumptions = {},
  keyInsights = null,
  breakdownRows = null,
  renderedAssumptions = null,
  onNavigateToPlayBook = null,
}) {
  const [activeTab, setActiveTab] = React.useState(0);
  const hasReportContent =
    !!costData || !!riskData || !!substituteData || !!productImageUrl || !!keyInsights;

  const resolvedRows = costRows || [];
  const resolvedTotal = Number.isFinite(Number(costTotal))
    ? Number(costTotal)
    : resolvedRows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);

  const reportPayload = React.useMemo(
    () =>
      JSON.stringify({
        productName,
        productData,
        productImageUrl,
        costRows: resolvedRows,
        costTotal: resolvedTotal,
        brandColor,
      }),
    [brandColor, productData, productImageUrl, productName, resolvedRows, resolvedTotal],
  );

  // Debug logging
  React.useEffect(() => {
    console.log("📊 CookiesResultsSection - assumptions prop:", assumptions);
    console.log("📊 CookiesResultsSection - assumptions keys:", Object.keys(assumptions));
    console.log("📊 CookiesResultsSection - assumptions.Formulation:", assumptions?.Formulation);
    console.log("📊 CookiesResultsSection - assumptions.Process:", assumptions?.Process);
    console.log("📊 CookiesResultsSection - assumptions.Packaging:", assumptions?.Packaging);
  }, [assumptions]);

  React.useEffect(() => {
    console.log("🔑 CookiesResultsSection - keyInsights prop:", keyInsights);
    console.log("🔑 CookiesResultsSection - keyInsights type:", typeof keyInsights);
    if (keyInsights) {
      console.log("🔑 CookiesResultsSection - keyInsights keys:", Object.keys(keyInsights));
      console.log("🔑 CookiesResultsSection - keyInsights.cost_savings:", keyInsights.cost_savings);
      console.log("🔑 CookiesResultsSection - keyInsights.strategic:", keyInsights.strategic);
    }
    console.log(
      "🔑 Condition check - cost_savings length > 0?:",
      keyInsights?.cost_savings?.length > 0
    );
    console.log("🔑 Condition check - strategic length > 0?:", keyInsights?.strategic?.length > 0);
  }, [keyInsights]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const isPlayBookNavigable = typeof onNavigateToPlayBook === "function";
  const handlePlayBookNavigation = () => {
    if (isPlayBookNavigable) {
      onNavigateToPlayBook();
    }
  };

  // Tab configuration
  const tabs = [
    { label: "Cost Breakdown", id: "intelligent" },
    { label: "Risk Analysis", id: "risk" },
    { label: "Substitutes", id: "substitute" },
  ];

  return (
    <Box data-report-export-root="true" data-report-ready={hasReportContent ? "true" : "false"}>
      <script type="application/json" data-report-payload>
        {reportPayload}
      </script>
      {/* Tabs Navigation */}
      <Box sx={{ mb: 3, borderBottom: `1px solid ${perlColors.border}` }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            "& .MuiTab-root": {
              color: perlColors.textSecondary,
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              fontFamily: perlTypography.family,
              letterSpacing: "0.3px",
              "&.Mui-selected": {
                color: brandColor,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: brandColor,
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.id} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        {/* Cost Breakdown Tab */}
        {activeTab === 0 && (
          <Box>
            {/* Message to update formulation/playbook if needed */}
            {costData && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: `${brandColor}10`,
                  border: `1px solid ${brandColor}40`,
                  borderRadius: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontFamily: perlTypography.family,
                    color: perlColors.textPrimary,
                  }}
                >
                  To update the cost analysis, modify the{" "}
                  <Box component="span" sx={{ color: brandColor, fontWeight: 600 }}>
                    Formulation
                  </Box>{" "}
                  or{" "}
                  <Box
                    component="span"
                    role={isPlayBookNavigable ? "button" : undefined}
                    tabIndex={isPlayBookNavigable ? 0 : undefined}
                    onClick={isPlayBookNavigable ? handlePlayBookNavigation : undefined}
                    onKeyDown={
                      isPlayBookNavigable
                        ? (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handlePlayBookNavigation();
                            }
                          }
                        : undefined
                    }
                    sx={{
                      color: brandColor,
                      fontWeight: 600,
                      cursor: isPlayBookNavigable ? "pointer" : "default",
                      textDecoration: isPlayBookNavigable ? "underline" : "none",
                    }}
                  >
                    PlayBook
                  </Box>{" "}
                  parameters and click 'Quick Cost' to recalculate
                </Typography>
              </Box>
            )}

            {/* Split Layout: Image on Left, Table + Chart on Right */}
            <Box data-report-cost-layout="true" sx={{ display: "flex", gap: 3, mb: 3 }}>
              {/* Left Side - Product Image */}
              <ImageWithRegenerate
                imageUrl={productImageUrl}
                loading={generatingImage}
                onRegenerate={onRegenerateImage}
                productName={productName}
                brandColor={brandColor}
                height={540}
              />

              {/* Right Side - Cost Table + Chart Stacked */}
              <Box
                data-report-cost-details="true"
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "540px",
                }}
              >
                {/* Cost Table - Takes remaining space */}
                <Box
                  data-report-cost-table="true"
                  sx={{ flex: 1, mb: 2, display: "flex", flexDirection: "column" }}
                >
                  <PerlCostTable
                    breakdown={costData?.breakdown}
                    rows={resolvedRows}
                    total={resolvedTotal}
                    brandColor={brandColor}
                  />
                </Box>

                {/* Waterfall Chart - Fixed height */}
                <Box data-report-cost-chart="true" sx={{ height: "240px", flexShrink: 0 }}>
                  <CostWaterfall
                    breakdown={costData?.breakdown}
                    rows={resolvedRows}
                    total={resolvedTotal}
                  />
                </Box>
              </Box>
            </Box>

            {/* Assumptions Section - Grouped by Subsection */}
            {assumptions && Object.keys(assumptions).length > 0 && (
              <Paper
                sx={{
                  p: 3,
                  bgcolor: perlColors.surface,
                  border: `1px solid ${perlColors.border}`,
                  borderRadius: 2,
                  mt: 3,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "18px",
                    fontFamily: perlTypography.family,
                    letterSpacing: "0.3px",
                    color: perlColors.textPrimary,
                  }}
                >
                  Assumptions
                </Typography>

                {/* Render each subsection if available */}
                {assumptions.Formulation && assumptions.Formulation.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontSize: "13px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                        letterSpacing: "0.3px",
                      }}
                    >
                      Formulation
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {assumptions.Formulation.map((assumption, idx) => (
                        <Typography
                          key={idx}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            lineHeight: "20px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {assumption}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {assumptions.Process && assumptions.Process.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontSize: "13px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                        letterSpacing: "0.3px",
                      }}
                    >
                      Process
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {assumptions.Process.map((assumption, idx) => (
                        <Typography
                          key={idx}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            lineHeight: "20px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {assumption}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {assumptions.Packaging && assumptions.Packaging.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontSize: "13px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                        letterSpacing: "0.3px",
                      }}
                    >
                      Packaging
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {assumptions.Packaging.map((assumption, idx) => (
                        <Typography
                          key={idx}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            lineHeight: "20px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {assumption}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {assumptions.OHP && assumptions.OHP.length > 0 && (
                  <Box sx={{ mb: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontSize: "13px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                        letterSpacing: "0.3px",
                      }}
                    >
                      OHP
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {assumptions.OHP.map((assumption, idx) => (
                        <Typography
                          key={idx}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            lineHeight: "20px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {assumption}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            )}

            {/* Key Insights Section */}
            {keyInsights &&
              (keyInsights.cost_savings?.length > 0 || keyInsights.strategic?.length > 0) && (
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: perlColors.surface,
                    border: `1px solid ${perlColors.border}`,
                    borderRadius: 2,
                    mt: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      fontFamily: perlTypography.family,
                      letterSpacing: "0.3px",
                      color: perlColors.textPrimary,
                    }}
                  >
                    Key Insights
                  </Typography>

                  {/* Cost Savings */}
                  {keyInsights.cost_savings?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          fontSize: "13px",
                          fontWeight: 600,
                          fontFamily: perlTypography.family,
                          color: perlColors.textPrimary,
                        }}
                      >
                        Cost Savings Opportunities
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {keyInsights.cost_savings.map((insight, idx) => {
                          // Handle both string and object formats
                          let displayText;
                          if (typeof insight === "string") {
                            displayText = insight;
                          } else {
                            // Format: icon + savings (bold) + " - " + action
                            displayText = (
                              <>
                                {insight.icon && (
                                  <Box component="span" sx={{ mr: 0.5 }}>
                                    {insight.icon}
                                  </Box>
                                )}
                                {insight.savings && (
                                  <Box
                                    component="span"
                                    sx={{ fontWeight: 600, color: perlColors.textPrimary }}
                                  >
                                    {insight.savings}
                                  </Box>
                                )}
                                {insight.action && (
                                  <>
                                    {insight.savings && " - "}
                                    {insight.action}
                                  </>
                                )}
                              </>
                            );
                          }

                          return (
                            <Typography
                              key={idx}
                              component="li"
                              variant="body2"
                              sx={{
                                mb: 1.5,
                                fontSize: "14px",
                                fontFamily: perlTypography.family,
                                color: perlColors.textSecondary,
                                lineHeight: "20px",
                                letterSpacing: "0.3px",
                              }}
                            >
                              {displayText}
                            </Typography>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Strategic Insights */}
                  {keyInsights.strategic?.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          fontSize: "13px",
                          fontWeight: 600,
                          fontFamily: perlTypography.family,
                          color: perlColors.textPrimary,
                        }}
                      >
                        Strategic Recommendations
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {keyInsights.strategic.map((insight, idx) => {
                          // Strategic items are typically strings with icon already included
                          const text = typeof insight === "string" ? insight : insight.action || "";

                          return (
                            <Typography
                              key={idx}
                              component="li"
                              variant="body2"
                              sx={{
                                mb: 1.5,
                                fontSize: "14px",
                                fontFamily: perlTypography.family,
                                color: perlColors.textSecondary,
                                lineHeight: "20px",
                                letterSpacing: "0.3px",
                              }}
                            >
                              {text}
                            </Typography>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}
          </Box>
        )}

        {/* Risk Analysis Tab */}
        {activeTab === 1 && (
          <Box>
            {riskData ? (
              <Box>
                {/* Summary (New Schema) */}
                {riskData.summary && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Risk Summary
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontFamily: perlTypography.family,
                        color: perlColors.textSecondary,
                        lineHeight: "20px",
                      }}
                    >
                      {riskData.summary}
                    </Typography>
                  </Paper>
                )}

                {/* Overall Risk Score (Old Schema - fallback) */}
                {!riskData.summary && riskData.overall_risk_score && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1,
                        fontSize: "16px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Overall Risk Score: {riskData.overall_risk_score}/10
                    </Typography>
                  </Paper>
                )}

                {/* Risk Matrix (New Schema) */}
                {riskData.matrix && riskData.matrix.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Risk Category Scores
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {riskData.matrix.map((item, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            flex: "0 1 calc(33.333% - 16px)",
                            p: 2,
                            bgcolor: `${brandColor}10`,
                            border: `1px solid ${brandColor}40`,
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              fontFamily: perlTypography.family,
                              color: perlColors.textSecondary,
                              mb: 0.5,
                            }}
                          >
                            {item.category}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "20px",
                              fontWeight: 600,
                              fontFamily: perlTypography.family,
                              color: brandColor,
                            }}
                          >
                            {item.score}/10
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                )}

                {/* Risk Items (New Schema) */}
                {riskData.risk_items && riskData.risk_items.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Identified Risks
                    </Typography>
                    {riskData.risk_items.map((risk, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          mb: 3,
                          pb: 3,
                          borderBottom:
                            idx < riskData.risk_items.length - 1
                              ? `1px solid ${perlColors.border}`
                              : "none",
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              fontFamily: perlTypography.family,
                              color: perlColors.textPrimary,
                            }}
                          >
                            {risk.title}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 500,
                                fontFamily: perlTypography.family,
                                color: brandColor,
                                bgcolor: `${brandColor}20`,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              {risk.type}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 500,
                                fontFamily: perlTypography.family,
                                color: perlColors.textSecondary,
                                bgcolor: perlColors.border,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              {risk.time_horizon}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            mb: 1.5,
                          }}
                        >
                          {risk.impact_note}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 3, mb: 1.5 }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textPrimary,
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 500 }}>
                              Severity:
                            </Box>{" "}
                            {risk.severity}/10
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textPrimary,
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 500 }}>
                              Probability:
                            </Box>{" "}
                            {risk.probability}/10
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textPrimary,
                            mb: 0.5,
                          }}
                        >
                          <Box component="span" sx={{ fontWeight: 500 }}>
                            Mitigation:
                          </Box>{" "}
                          {risk.mitigation}
                        </Typography>
                        {risk.kpis && risk.kpis.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 500,
                                fontFamily: perlTypography.family,
                                color: perlColors.textSecondary,
                                mb: 0.5,
                              }}
                            >
                              KPIs to Monitor:
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                              {risk.kpis.map((kpi, kpiIdx) => (
                                <Typography
                                  key={kpiIdx}
                                  sx={{
                                    fontSize: "11px",
                                    fontFamily: perlTypography.family,
                                    color: perlColors.textSecondary,
                                    bgcolor: perlColors.border,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                  }}
                                >
                                  {kpi}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Paper>
                )}

                {/* Supply Chain Risks (Old Schema - fallback) */}
                {!riskData.risk_items &&
                  riskData.supply_chain_risks &&
                  riskData.supply_chain_risks.length > 0 && (
                    <Paper
                      sx={{
                        p: 3,
                        mb: 3,
                        bgcolor: perlColors.surface,
                        border: `1px solid ${perlColors.border}`,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 2,
                          fontSize: "14px",
                          fontWeight: 600,
                          fontFamily: perlTypography.family,
                          color: perlColors.textPrimary,
                        }}
                      >
                        Supply Chain Risks
                      </Typography>
                      {riskData.supply_chain_risks.map((risk, idx) => (
                        <Box key={idx} sx={{ mb: 2 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              fontFamily: perlTypography.family,
                              color: perlColors.textPrimary,
                              mb: 0.5,
                            }}
                          >
                            {risk.ingredient || risk.area} - Severity: {risk.severity}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textSecondary,
                            }}
                          >
                            {risk.description || risk.risk}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}

                {/* Cost Risks (Old Schema - fallback) */}
                {!riskData.risk_items && riskData.cost_risks && riskData.cost_risks.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Cost Risks
                    </Typography>
                    {riskData.cost_risks.map((risk, idx) => (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            fontFamily: perlTypography.family,
                            color: perlColors.textPrimary,
                            mb: 0.5,
                          }}
                        >
                          {risk.area || risk.ingredient} - Severity: {risk.severity}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                          }}
                        >
                          {risk.description || risk.risk}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}

                {/* Top Concerns (Old Schema - fallback) */}
                {!riskData.risk_items &&
                  riskData.top_concerns &&
                  riskData.top_concerns.length > 0 && (
                    <Paper
                      sx={{
                        p: 3,
                        bgcolor: perlColors.surface,
                        border: `1px solid ${perlColors.border}`,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 2,
                          fontSize: "14px",
                          fontWeight: 600,
                          fontFamily: perlTypography.family,
                          color: perlColors.textPrimary,
                        }}
                      >
                        Top Concerns
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {riskData.top_concerns.map((concern, idx) => (
                          <Typography
                            key={idx}
                            component="li"
                            variant="body2"
                            sx={{
                              mb: 1,
                              fontSize: "14px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textSecondary,
                              lineHeight: "20px",
                            }}
                          >
                            {concern}
                          </Typography>
                        ))}
                      </Box>
                    </Paper>
                  )}

                {/* Assumptions (New Schema) */}
                {riskData.assumptions && (
                  <Paper
                    sx={{
                      p: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Assumptions
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontFamily: perlTypography.family,
                        color: perlColors.textSecondary,
                        lineHeight: 1.6,
                      }}
                    >
                      {riskData.assumptions}
                    </Typography>
                  </Paper>
                )}
              </Box>
            ) : (
              <Typography sx={{ color: perlColors.textSecondary }}>
                Risk analysis data not available
              </Typography>
            )}
          </Box>
        )}

        {/* Substitutes Tab */}
        {activeTab === 2 && (
          <Box>
            {substituteData ? (
              <Box>
                {/* Summary (New Schema) */}
                {substituteData.summary && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Executive Summary
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontFamily: perlTypography.family,
                        color: perlColors.textSecondary,
                        lineHeight: 1.6,
                      }}
                    >
                      {substituteData.summary}
                    </Typography>
                  </Paper>
                )}

                {/* Substitution Ideas (New Schema) */}
                {substituteData.ideas && substituteData.ideas.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Substitution Ideas
                    </Typography>
                    {substituteData.ideas.map((idea) => (
                      <Box
                        key={idea.id}
                        sx={{
                          mb: 3,
                          pb: 3,
                          borderBottom:
                            idea.id !== substituteData.ideas[substituteData.ideas.length - 1].id
                              ? `1px solid ${perlColors.border}`
                              : "none",
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              fontFamily: perlTypography.family,
                              color: perlColors.textPrimary,
                            }}
                          >
                            {idea.id}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 500,
                                fontFamily: perlTypography.family,
                                color: brandColor,
                                bgcolor: `${brandColor}20`,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              {idea.domain}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 500,
                                fontFamily: perlTypography.family,
                                color: perlColors.textSecondary,
                                bgcolor: perlColors.border,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              {idea.timeline_weeks} weeks
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textPrimary,
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 500 }}>
                              Current:
                            </Box>{" "}
                            {idea.current_item}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textPrimary,
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 500 }}>
                              Proposed:
                            </Box>{" "}
                            {idea.proposed_item}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 3, mb: 1.5 }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textPrimary,
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 500 }}>
                              Cost Delta:
                            </Box>{" "}
                            {idea.estimated_cost_delta_per_unit}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textPrimary,
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 500 }}>
                              Feasibility:
                            </Box>{" "}
                            {idea.feasibility_score}/10
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            mb: 1.5,
                          }}
                        >
                          {idea.risk_note}
                        </Typography>
                        {idea.validation_steps && idea.validation_steps.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 500,
                                fontFamily: perlTypography.family,
                                color: perlColors.textSecondary,
                                mb: 0.5,
                              }}
                            >
                              Validation Steps:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                              {idea.validation_steps.map((step, stepIdx) => (
                                <Typography
                                  key={stepIdx}
                                  component="li"
                                  sx={{
                                    fontSize: "12px",
                                    fontFamily: perlTypography.family,
                                    color: perlColors.textSecondary,
                                    mb: 0.5,
                                  }}
                                >
                                  {step}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Paper>
                )}

                {/* Prioritization (New Schema) */}
                {substituteData.prioritization && substituteData.prioritization.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Priority Ranking
                    </Typography>
                    <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                      {substituteData.prioritization.map((item, idx) => (
                        <Typography
                          key={idx}
                          component="li"
                          sx={{
                            fontSize: "13px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textPrimary,
                            mb: 1,
                            fontWeight: idx === 0 ? 500 : 400,
                          }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </Paper>
                )}

                {/* Assumptions (New Schema) */}
                {substituteData.assumptions && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Assumptions
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontFamily: perlTypography.family,
                        color: perlColors.textSecondary,
                        lineHeight: 1.6,
                      }}
                    >
                      {substituteData.assumptions}
                    </Typography>
                  </Paper>
                )}

                {/* Overall Recommendations (Old Schema - fallback) */}
                {!substituteData.ideas && substituteData.overall_recommendations && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Overall Recommendations
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontFamily: perlTypography.family,
                        color: perlColors.textSecondary,
                        lineHeight: "20px",
                      }}
                    >
                      {substituteData.overall_recommendations}
                    </Typography>
                  </Paper>
                )}
              </Box>
            ) : (
              <Typography sx={{ color: perlColors.textSecondary }}>
                Substitute data not available
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
